import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Pill, History, X, Search, Check } from 'lucide-react';
import { headachesAPI } from '../../services/api';

/**
 * Selector de medicamento con buscador.
 *
 * En vez de un desplegable en linea (que en movil pelea con el teclado y con el
 * overflow del modal contenedor), abre un MODAL propio por encima: buscador
 * arriba, lista con scroll debajo. Asi el teclado y la lista conviven sin taparse
 * ni recortarse. El modal se renderiza en un portal para escapar de cualquier
 * ancestro transformado (las animaciones de framer-motion crean contexto de
 * apilamiento y recortarian un hijo fixed).
 *
 * Prioriza el historial del usuario (ordenado por frecuencia) sobre un catalogo
 * generico, porque la gente repite tratamiento y asi se reducen variantes escritas
 * a mano del mismo farmaco. Permite ademas escribir un nombre libre.
 */

// Catalogo base. Solo complementa al historial.
const COMMON_MEDICATIONS = [
  'Paracetamol',
  'Paracetamol 1000mg',
  'Ibuprofeno',
  'Ibuprofeno 600mg',
  'Nolotil',
  'Aspirina',
  'Enantyum',
  'Naproxeno',
  'Naproxeno 550mg',
  'Espidifen',
  'Gelocatil',
  'Frenadol',
  'Sumatriptán 50mg',
  'Zomig 2.5mg',
  'Rizatriptán 10mg',
  'Almotriptán 12.5mg',
];

interface MedicationAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

interface Suggestion {
  name: string;
  fromHistory: boolean;
  uses?: number;
}

export const MedicationAutocomplete: React.FC<MedicationAutocompleteProps> = ({
  value,
  onChange,
  placeholder = 'Ej: Paracetamol, Ibuprofeno...',
  className = 'input',
}) => {
  const [history, setHistory] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Alto del teclado y alto visible, via visualViewport, para levantar la hoja por
  // encima del teclado en vez de que quede oculta detras.
  const [kbHeight, setKbHeight] = useState(0);
  const [visibleH, setVisibleH] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    const loadHistory = async () => {
      try {
        const response = await headachesAPI.getMedications();
        if (!cancelled && response.success && response.data) {
          setHistory(
            response.data.medications.map((m) => ({ name: m.name, fromHistory: true, uses: m.uses }))
          );
        }
      } catch {
        // Sin historial se sigue funcionando con el catalogo generico
      }
    };
    loadHistory();
    return () => {
      cancelled = true;
    };
  }, []);

  const allSuggestions = useMemo<Suggestion[]>(() => {
    const seen = new Set(history.map((h) => h.name.toLowerCase()));
    const generic = COMMON_MEDICATIONS.filter((m) => !seen.has(m.toLowerCase())).map((name) => ({
      name,
      fromHistory: false,
    }));
    return [...history, ...generic];
  }, [history]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allSuggestions.slice(0, 40);
    return allSuggestions.filter((s) => s.name.toLowerCase().includes(q)).slice(0, 40);
  }, [query, allSuggestions]);

  const exactMatch = useMemo(
    () => filtered.some((s) => s.name.toLowerCase() === query.trim().toLowerCase()),
    [filtered, query]
  );

  const openPicker = () => {
    setQuery('');
    setOpen(true);
  };
  const select = (name: string) => {
    onChange(name);
    setOpen(false);
  };
  const confirmCustom = () => {
    const t = query.trim();
    if (t) select(t);
  };

  // Enfocar el buscador al abrir (con un pequeño retardo para dar tiempo a montar).
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => searchRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [open]);

  // Seguir el teclado: kbHeight es lo que ocupa el teclado por debajo del viewport
  // visible; visibleH es el alto realmente visible. Con esto la hoja se coloca justo
  // encima del teclado y limita su altura a lo que se ve.
  useEffect(() => {
    if (!open) return;
    const vv = window.visualViewport;
    const update = () => {
      if (vv) {
        setKbHeight(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
        setVisibleH(vv.height);
      } else {
        setKbHeight(0);
        setVisibleH(window.innerHeight);
      }
    };
    update();
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  return (
    <>
      {/* Disparador: parece un campo, pero abre el modal selector */}
      <button
        type="button"
        onClick={openPicker}
        className={`${className} flex items-center justify-between gap-2 text-left`}
      >
        <span className={value ? 'text-[var(--color-text)] truncate' : 'text-[var(--color-text-muted)] truncate'}>
          {value || placeholder}
        </span>
        <Pill size={16} className="text-[var(--color-text-muted)] flex-shrink-0" />
      </button>

      {createPortal(
        <AnimatePresence>
          {open && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[120]"
                onClick={() => setOpen(false)}
              />
              <div
                className="fixed inset-0 z-[121] flex items-end sm:items-center justify-center sm:p-4 pointer-events-none"
                style={{ paddingBottom: kbHeight, transition: 'padding-bottom 0.2s ease' }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 30, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 30, scale: 0.98 }}
                  transition={{ type: 'spring', damping: 28, stiffness: 320 }}
                  className="pointer-events-auto w-full sm:max-w-md bg-[var(--color-surface)] rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden"
                  style={{ maxHeight: Math.min((visibleH || window.innerHeight) - 16, window.innerHeight * 0.85) }}
                >
                  {/* Cabecera + buscador */}
                  <div className="p-4 border-b border-[var(--color-border)] flex-shrink-0">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-[var(--color-text)]">Medicación</h3>
                      <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="p-1.5 rounded-lg hover:bg-[var(--color-bg)] transition-colors"
                      >
                        <X size={20} className="text-[var(--color-text-muted)]" />
                      </button>
                    </div>
                    <div className="relative">
                      <Search
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
                      />
                      <input
                        ref={searchRef}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            confirmCustom();
                          }
                        }}
                        placeholder="Buscar o escribir un nombre…"
                        className="input pl-9"
                        autoComplete="off"
                      />
                    </div>
                  </div>

                  {/* Lista */}
                  <div
                    className="overflow-y-auto py-1"
                    style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom, 0.5rem))' }}
                  >
                    {query.trim() && !exactMatch && (
                      <button
                        type="button"
                        onClick={confirmCustom}
                        className="w-full px-4 py-3 text-left flex items-center gap-2.5 hover:bg-[var(--color-bg)] transition-colors"
                      >
                        <span className="w-[15px] flex-shrink-0 flex justify-center">
                          <Check size={16} className="text-[var(--color-accent)]" />
                        </span>
                        <span className="text-[var(--color-text)]">
                          Usar «{query.trim()}»
                        </span>
                      </button>
                    )}
                    {filtered.map((s) => (
                      <button
                        key={s.name}
                        type="button"
                        onClick={() => select(s.name)}
                        className="w-full px-4 py-3 text-left flex items-center gap-2.5 hover:bg-[var(--color-bg)] transition-colors"
                      >
                        {s.fromHistory ? (
                          <History size={15} className="text-[var(--color-accent)] flex-shrink-0" />
                        ) : (
                          <Pill size={15} className="text-[var(--color-text-muted)] flex-shrink-0" />
                        )}
                        <span className="flex-1 truncate text-[var(--color-text)]">{s.name}</span>
                        {value === s.name && <Check size={16} className="text-[var(--color-accent)] flex-shrink-0" />}
                        {s.fromHistory && s.uses !== undefined && (
                          <span className="text-xs text-[var(--color-text-muted)] flex-shrink-0">{s.uses}×</span>
                        )}
                      </button>
                    ))}
                    {filtered.length === 0 && (
                      <p className="px-4 py-8 text-center text-sm text-[var(--color-text-muted)]">
                        {query.trim() ? 'Pulsa «Usar…» para guardarlo' : 'Sin sugerencias'}
                      </p>
                    )}
                  </div>
                </motion.div>
              </div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};
